import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ID, Region } from "@gooner/shared";
import { syncLeaderOf } from "@gooner/shared";
import { regionChromeButton } from "../ui/primitives";
import { syncTintMap } from "../lib/syncTints";
import { IconLink, IconLinkOff } from "../ui/icons";
import { track } from "../../analytics";

/**
 * The link toggle riding every region tile: sync without leaving the surface. Shared by
 * the editor's canvas and the screen's hover chrome — one definition of what the link
 * icon does, wherever a tile wears it.
 *
 * Slashed link = independent; click opens a menu of every region this one could follow
 * (same candidate rule as SyncControl — no followers offered, so no chains). Solid link =
 * part of a pair, follower or leader alike; click DISCONNECTS with both sides keeping the
 * shared feed (`unlinkAdoptShared` — the panel's Unsync is the one that restores the old
 * snapshot instead). Every pointer event stops here: the tile underneath starts a MOVE
 * (or, on the wall, opens the region popup) on its own handler, and a menu that dragged
 * the region while you picked from it is a trap.
 *
 * The menu is `position: fixed` so it escapes the tile's `overflow-hidden` — a dropdown
 * clipped to a Checkers-sized tile would show half an option.
 */
export function RegionLinkButton({
  region,
  allRegions,
  onLink,
  onUnlink,
  onMenuToggle,
  onCandidateHover,
}: {
  region: Region;
  allRegions: Region[];
  onLink: (targetId: ID) => void;
  onUnlink: () => void;
  /**
   * The menu opened or closed. The screen's hover chrome pins itself open on true:
   * the menu portals BELOW the tile, so the pointer leaves the tile to reach it, and
   * without the pin the mouseleave would unmount this button and the menu with it.
   * The editor's canvas has no such hazard and simply omits this.
   */
  onMenuToggle?: (open: boolean) => void;
  /**
   * The pointer is on a candidate row (id), or left it (null). The screen's hover
   * chrome crosslights that region on the glass so "which tile is that?" is answered
   * by looking up, not by reading names. Fired with null on every close path — the
   * setMenu funnel below sees them all — so a peek can never outlive the menu.
   * The editor omits this like it omits onMenuToggle.
   */
  onCandidateHover?: (id: ID | null) => void;
}) {
  const [menu, setMenuState] = useState<{ top: number; left: number } | null>(null);
  const onMenuToggleRef = useRef(onMenuToggle);
  onMenuToggleRef.current = onMenuToggle;
  const onCandidateHoverRef = useRef(onCandidateHover);
  onCandidateHoverRef.current = onCandidateHover;
  // Notified OUTSIDE the state updater: every setMenu call sits in an event handler,
  // and the host's reaction is its own setState — fired from inside the updater it
  // would be a cross-component update mid-render, which React rightly flags. The ref
  // dedupes so re-anchoring an already-open menu is not a second "opened".
  const menuOpenRef = useRef(false);
  const setMenu = (m: { top: number; left: number } | null) => {
    const open = m !== null;
    if (menuOpenRef.current !== open) {
      menuOpenRef.current = open;
      onMenuToggleRef.current?.(open);
      // Whatever closed the menu (pick, outside, Escape, toggle) ends the peek too.
      if (!open) onCandidateHoverRef.current?.(null);
    }
    setMenuState(m);
  };
  const rootRef = useRef<HTMLDivElement>(null);
  /** The portaled menu — outside-close must test both containers. */
  const menuRef = useRef<HTMLDivElement>(null);
  const linked =
    syncLeaderOf(region, allRegions) !== null ||
    allRegions.some((r) => r.syncWith === region.id && syncLeaderOf(r, allRegions) !== null);
  const candidates = allRegions.filter((r) => r.id !== region.id && !r.syncWith);
  // Same colours the canvas badges wear — see syncTintMap.
  const menuTints = syncTintMap(allRegions);
  const label = (r: Region) =>
    r.name?.trim() || `Region ${allRegions.findIndex((x) => x.id === r.id) + 1}`;

  useEffect(() => {
    if (!menu) return;
    // CAPTURE phase, deliberately: the canvas tiles stop pointerdown propagation
    // (a click on a tile is a move, not a page click), which starved a bubble-phase
    // listener here and left the menu floating while you dragged another region.
    // Capture runs before anyone can stop the event. The menu itself is portaled,
    // so "outside" means outside the button AND outside the menu.
    const close = (e: PointerEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setMenu(null);
    };
    // Escape closes the menu and ONLY the menu — stopPropagation keeps the
    // editor's Escape-deselect (a window listener, later on the path) out of it.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setMenu(null);
    };
    document.addEventListener("pointerdown", close, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", close, true);
      document.removeEventListener("keydown", onKey, true);
    };
    // setMenu is a stable wrapper around setState; only the menu's presence matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu]);

  // Unmounted with the menu open (the chrome unmounts when the pointer strays and the
  // lock releases): the setMenu funnel never runs, so end a live peek here.
  useEffect(
    () => () => {
      if (menuOpenRef.current) onCandidateHoverRef.current?.(null);
    },
    [],
  );

  if (!linked && candidates.length === 0) return null; // nothing to link to, nothing to say

  return (
    <div
      ref={rootRef}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        title={linked ? "Unlink — both regions keep this feed" : "Link to another region"}
        onClick={(e) => {
          // Drop focus so a Space pressed next does what the surface says it does
          // (skip, on the wall) instead of re-clicking this button.
          e.currentTarget.blur();
          if (linked) {
            setMenu(null);
            track("regions_unlinked");
            onUnlink();
            return;
          }
          if (menu) {
            setMenu(null);
            return;
          }
          const rect = e.currentTarget.getBoundingClientRect();
          setMenu({ top: rect.bottom + 4, left: rect.right });
        }}
        className={regionChromeButton(linked)}
      >
        {linked ? <IconLink width={14} height={14} /> : <IconLinkOff width={14} height={14} />}
      </button>
      {/* Portaled to <body>: rendered in place, the tile's stacking context (and its
          backdrop-blur ancestors, which re-anchor `fixed`) let LATER tiles paint over
          the open menu. The body owns no stacking context, so z-50 finally means it. */}
      {menu ? (
        createPortal(
        <div
          ref={menuRef}
          className="fixed z-50 min-w-32 -translate-x-full rounded-md border border-[var(--color-edge)] bg-[var(--color-panel-2)] p-1 shadow-lg"
          style={{ top: menu.top, left: menu.left }}
          // Keeps the editor's document-level deselect listener out of menu clicks.
          // (React 18 delegates portal events at the portal container, so this stops
          // the native event before it bubbles to document — by design, not luck.)
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
            Link with
          </div>
          {candidates.map((r) => (
            <button
              key={r.id}
              type="button"
              onMouseEnter={() => onCandidateHover?.(r.id)}
              onMouseLeave={() => onCandidateHover?.(null)}
              onClick={() => {
                setMenu(null);
                track("regions_linked");
                onLink(r.id);
              }}
              className="block w-full truncate rounded px-2 py-1 text-left text-xs text-zinc-200 transition-colors hover:bg-[var(--color-accent)]/20"
              // A candidate already in a sync group wears that group's colour — the
              // same one its tile badge wears — so you can see what you would join.
              style={{ color: menuTints.get(r.id) }}
            >
              {label(r)}
            </button>
          ))}
        </div>,
        document.body,
        )
      ) : null}
    </div>
  );
}
